import React, { useEffect, useMemo, useState } from "react";

// Timer ajustado para os dados do Status Diário (target HH:MM, inícios/fins)

const TIMER_DEBUG =
  typeof window !== "undefined" && window.localStorage?.getItem("sd.timer.debug") === "1";

const toSec = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // HH:MM or HH:MM:SS
  let m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m) {
    const h = Number(m[1]);
    const mm = Number(m[2]);
    const ss = Number(m[3] || 0);
    return h * 3600 + mm * 60 + ss;
  }
  // numeric => minutos
  const n = Number(s.replace(",", "."));
  if (Number.isFinite(n)) return Math.floor(n * 60);
  return null;
};

const toTs = (timeStr, day) => {
  if (!timeStr) return null;
  const s = String(timeStr).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const ss = Number(m[3] || 0);
  const isoDay = day || new Date().toISOString().slice(0, 10);
  const d = new Date(`${isoDay}T${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}:${String(ss).padStart(2, "0")}`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const formatHM = (sec) => {
  if (sec == null) return "—";
  const neg = sec < 0;
  const abs = Math.abs(Math.floor(sec));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const txt = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return neg ? `-${txt}` : txt;
};

function computeState({
  target,
  previsao,
  fallbackMetaSeconds = null,
  day,
  primeiro_inicio,
  primeiro_fim,
  segundo_inicio,
  segundo_fim,
  terceiro_inicio,
  terceiro_fim,
  nowTs,
}) {
  const metaSeconds = toSec(target) ?? (Number.isFinite(fallbackMetaSeconds) ? fallbackMetaSeconds : null);
  // meta pode ser nula para pre-countdown; só impede se falta previsao também
  // se não há meta e não há previsão, retorna waiting neutro
  const hasTarget = metaSeconds != null && metaSeconds > 0;
  const waitingLabel = metaSeconds != null ? formatHM(metaSeconds) : "—";

  const pairs = [
    { start: toTs(primeiro_inicio, day), end: toTs(primeiro_fim, day) },
    { start: toTs(segundo_inicio, day), end: toTs(segundo_fim, day) },
    { start: toTs(terceiro_inicio, day), end: toTs(terceiro_fim, day) },
  ];

  const hasStart = pairs.some((p) => p.start != null);
  const running = pairs.some((p) => p.start != null && p.end == null);
  const status = running ? "running" : hasStart ? "paused" : "waiting";

  // =========================
  // PREVISION COUNTDOWN (antes de iniciar)
  // =========================
  if (!hasStart && previsao) {
    const preTs = toTs(previsao, day);
    if (preTs) {
      const diffSec = Math.floor((preTs - nowTs) / 1000); // negativo = já passou
      const preExhausted = diffSec <= 0;
      // mapeia proximidade para faixas (azul)
      const mins = diffSec / 60;
      let preCls = "";
      if (preExhausted) preCls = "sd-pre--0-10 sd-pre--expired sd-pre--neg";
      else if (mins <= 5) preCls = "sd-pre--0-10";
      else if (mins <= 10) preCls = "sd-pre--10-40";
      else if (mins <= 30) preCls = "sd-pre--40-80";
      else preCls = "sd-pre--80-100";

      const preBlink = preExhausted || mins <= 2;

      return {
        metaSeconds: metaSeconds,
        remainingSigned: diffSec,
        remainingAbs: Math.abs(diffSec),
        // mantém o valor inicial (meta) na coluna tempo enquanto está em previsão
        label: waitingLabel,
        etaCls: preCls.trim(),
        blinkCls: preBlink ? "sd-pre--0-5-blink" : "",
        isCritical: preExhausted || mins <= 5,
        exhausted: preExhausted,
        ratioClamped: null,
        status: "pre",
        running: false,
        paused: false,
      };
    }
  }

  // se não tem meta válida e não está em pre, fica neutro
  if (!hasTarget) {
    return { label: waitingLabel, etaCls: "", blinkCls: "", isCritical: false, exhausted: false, ratioClamped: null, status };
  }

  let consumedMs = 0;
  for (const p of pairs) {
    if (p.start == null) continue;
    const end = p.end ?? nowTs;
    if (end > p.start) consumedMs += end - p.start;
  }
  const consumed = Math.floor(consumedMs / 1000);
  const remainingSigned = hasStart ? metaSeconds - consumed : metaSeconds; // se não iniciou, mostra 100%
  const ratioClamped = Math.max(0, Math.min(1, remainingSigned / Math.max(1, metaSeconds)));
  const exhausted = remainingSigned <= 0;
  const shouldBlink = running && (exhausted || ratioClamped <= 0.05);

  let etaCls = "";
  let blinkCls = "";
  // âœ… Linhas que já começaram (running ou paused) recebem faixa; blink só se running
  if (hasStart) {
    if (exhausted) {
      etaCls = "sd-eta--0-10 sd-eta--zero sd-eta--expired sd-eta--neg";
      if (shouldBlink) blinkCls = "sd-eta--0-5-blink";
    } else {
      if (ratioClamped <= 0.10) etaCls = "sd-eta--0-10";
      else if (ratioClamped <= 0.40) etaCls = "sd-eta--10-40";
      else if (ratioClamped <= 0.80) etaCls = "sd-eta--40-80";
      else etaCls = "sd-eta--80-100";
      if (shouldBlink) blinkCls = "sd-eta--0-5-blink";
    }
  }

  const pausedMarkerCls = status === "paused" ? "sd-eta--paused" : "";
  const label = formatHM(remainingSigned);
  // só consideramos crítico se estiver rodando
  const isCritical = status === "running" && (exhausted || ratioClamped <= 0.10);

  return {
    metaSeconds,
    remainingSigned,
    remainingAbs: Math.abs(Math.floor(remainingSigned)),
    label,
    etaCls: `${etaCls} ${pausedMarkerCls}`.trim(),
    blinkCls,
    isCritical,
    exhausted,
    ratioClamped,
    status,
    running,
    paused: status === "paused",
  };
}

export default function SdTempoTimer(props) {
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const state = useMemo(
    () =>
      computeState({
        target: props.target,
        previsao: props.previsao,
        fallbackMetaSeconds: props.timer_meta_seconds,
        day: props.day,
        primeiro_inicio: props.primeiro_inicio,
        primeiro_fim: props.primeiro_fim,
        segundo_inicio: props.segundo_inicio,
        segundo_fim: props.segundo_fim,
        terceiro_inicio: props.terceiro_inicio,
        terceiro_fim: props.terceiro_fim,
        nowTs,
      }),
    [props, nowTs]
  );

  if (TIMER_DEBUG) {
    console.log("[SdTimer]", { target: props.target, state });
  }

  const title =
    state.status === "waiting"
      ? `Meta: ${formatHM(state.metaSeconds)} — Aguardando início`
      : state.paused
      ? `Pausado — Restante: ${state.label}`
      : state.exhausted
      ? `Tempo estourado: ${state.label}`
      : `Contagem: ${state.label}`;

  return (
    <span
      className={`sd-timer ${state.etaCls} ${state.blinkCls} ${state.isCritical ? "sd-timer--critical" : ""}`.trim()}
      title={title}
      data-status={state.status}
      data-paused={state.paused ? "true" : "false"}
      data-critical={state.isCritical ? "true" : "false"}
      data-remaining={Math.max(0, state.remainingAbs || 0)}
      data-remaining-signed={state.remainingSigned ?? ""}
      data-negative={state.status === "running" && state.exhausted ? "1" : "0"}
      data-meta={state.metaSeconds ?? ""}
      data-ratio={state.ratioClamped ?? ""}
      data-eta={state.label}
      data-day={props.day || ""}
    >
      <span className="sd-timer__time">{state.label}</span>
    </span>
  );
}

// Proxy não usado aqui; mantém compat
export function SdTempoTimerProxy() {
  return <span className="sd-timer-proxy" aria-hidden="true" />;
}
